import { InterfaceJobFolderOptions, ProfileHeading } from '@components/templates/cv/sections/ProfileHeading';
import {
  CvDetailAvailableOperationsCurrentStatus,
  CvDetailObject,
} from '@components/templates/cv/interface/CvDetailInterface';
import { ProfileSkills } from '@components/templates/cv/sections/ProfileSkills';
import { ProfileExperience } from '@components/templates/cv/sections/ProfileExperience';
import { ProfileAbout } from '@components/templates/cv/sections/ProfileAbout';
import { ProfileStudies } from '@components/templates/cv/sections/ProfileStudies';
import { ProfilePreferences } from '@components/templates/cv/sections/ProfilePreferences';
import { TagCloud } from '@components/templates/cv/sections/TagCloud';
import { ProfileAgentData } from '@components/templates/cv/sections/ProfileAgentData';
import { EvaluateBia } from '@components/templates/cv/sections/EvaluateBia';
import { ProfileComments } from '@components/templates/cv/sections/ProfileComments';
import { ProfileInterview } from '@components/templates/cv/sections/ProfileInterview';
import { ProfileRequirementAnswers } from '@components/templates/cv/sections/ProfileRequirementAnswers';
import { ProfileApplications } from '@components/templates/cv/sections/ProfileApplications';
import { useAuthClient } from '@hooks/common/useAuthClient/useAuthClient';
import { VideoGalleryModal } from '@components/templates/cv/partials/modals/VideoGalleryModal';

interface IProps {
  cvDetail: CvDetailObject;
  applicantSeenContactStatus: boolean;
  cvCurrentJobUnlockFolder: InterfaceJobFolderOptions;
  setJobFolderInfo: (jobFolderInfo: InterfaceJobFolderOptions) => void;
  phoneHighlight: boolean;
  currentCVStatus: CvDetailAvailableOperationsCurrentStatus | undefined;
  jobFolderSlug?: string;
  jobFolderInfo?:InterfaceJobFolderOptions;
  jobFolderContext?: boolean;
  jobSlug?: string,
  openLinksInNewTab?: boolean;
  queryParams?: string;
  isCompany?: boolean;
  locale: Locale;
  jobFolderHighlight: boolean;
  revalidateCv?: (onSuccessCallback?: (cv: CvDetailObject) => void) => void;
}

export const ProfileResumeSections = ({
  setJobFolderInfo, cvDetail, jobFolderSlug, currentCVStatus, applicantSeenContactStatus, cvCurrentJobUnlockFolder,
  phoneHighlight, jobSlug, jobFolderHighlight, jobFolderContext, jobFolderInfo, openLinksInNewTab = false, queryParams, isCompany, locale, revalidateCv,
}: IProps) => {
  const resumeSearchObject = Object.fromEntries(new URLSearchParams(queryParams));

  const DEFAULT_OWN_CV = cvDetail?.isOwnCV;
  const { isAuth } = useAuthClient();

  const hasJobInterviews = cvDetail?.jobInterviewVideos && cvDetail?.jobInterviewVideos.length > 0;
  const hasCvInterviews = cvDetail?.interviewVideos && cvDetail?.interviewVideos.length > 0;
  const hasInterviews = hasJobInterviews || hasCvInterviews;

  return (
    <section>

      <div className="grid-cols-10 gap-5 rounded-md bg-surface py-5 lg:grid ">
        <div className="col-span-3 pr-0 lg:border-r lg:border-dotted lg:pr-4">
          <ProfileHeading
            applicantSeenContactStatus={applicantSeenContactStatus}
            jobFolderSlug={jobFolderSlug ?? ''}
            jobFolderContext={!!jobFolderContext}
            currentJobFolderItem={!jobFolderSlug ? cvCurrentJobUnlockFolder : {
              jobId: 0,
              title: '',
              jobSlug: jobFolderSlug ?? '',
            } as InterfaceJobFolderOptions}
            sendJobFolderInfoToParent={!jobFolderSlug ? setJobFolderInfo : () => undefined}
            phoneHighlight={phoneHighlight}
            cvDetail={cvDetail}
            currentCVStatus={currentCVStatus}
            openLinksInNewTab={openLinksInNewTab}
            isCompany={isCompany}
            locale={locale}
          />

          <ProfileAbout
            cvDetail={cvDetail}
            keyword={resumeSearchObject?.keyword}
          />

          <TagCloud
            cvDetail={cvDetail}
          />

          <ProfilePreferences
            cvDetail={cvDetail}
          />

        </div>
        <div className={`${(DEFAULT_OWN_CV || !(hasInterviews || isAuth)) ? 'col-span-7' : 'col-span-4'}`}>
          <ProfileExperience
            cvDetail={cvDetail}
            keyword={resumeSearchObject?.keyword}
          />

          <ProfileStudies
            cvDetail={cvDetail}
            keyword={resumeSearchObject?.keyword}
          />

          <ProfileSkills
            cvDetail={cvDetail}
            keyword={resumeSearchObject?.keyword}
          />
        </div>

        {!DEFAULT_OWN_CV && (isAuth || hasInterviews) && (
          <div className="col-span-3 lg:border-l lg:border-dotted">
            <div className="bg-surface pl-0 lg:pl-4">
              {/* Video Gallery - for unauthorized users it will show only job interviews, for authorized users all videos (cv interview, job interview and video cv) */}
              <VideoGalleryModal
                slug={cvDetail?.slug}
                mode="list"
                jobFolderInfo={jobFolderInfo}
                cvDetail={cvDetail as ApiSchema<'ResumeData'>}
                isAuth={!!isAuth}
                revalidateCv={revalidateCv}
                cvCurrentJobUnlockFolder={cvCurrentJobUnlockFolder}
              />

              {isAuth && (
                <>
                  {cvDetail?.agent !== undefined && cvDetail?.application?.isRecommended && (
                    <ProfileAgentData
                      cvDetail={cvDetail}
                    />
                  )}

                  <EvaluateBia cvDetail={cvDetail} />
                  <ProfileComments
                    cvDetail={cvDetail}
                    locale={locale}
                    jobSlug={jobSlug}
                    currentCVStatus={currentCVStatus}
                    isCompany={isCompany}
                  />
                  <ProfileInterview cvDetail={cvDetail} jobSlug={jobSlug} locale={locale} />
                  <ProfileRequirementAnswers cvDetail={cvDetail} />
                  <ProfileApplications
                    jobFolderHighlight={jobFolderHighlight}
                    cvDetail={cvDetail}
                  />
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
